import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { applyPlatformLanguage, normalizePlatformLanguage } from '../../utils/applyLanguage';
import './LanguageSwitcher.css';

/**
 * Language Switcher Component
 * 
 * Features:
 * - Switches between English and Arabic
 * - Syncs with backend (if user is logged in)
 * - Updates direction automatically
 * - Persists preference in localStorage
 */
const LanguageSwitcher = ({ className = '' }) => {
  const { t, i18n: i18nInstance } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18nInstance.language || 'en');
  const dropdownRef = useRef(null);

  // Update current language when i18n language changes
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setCurrentLang(normalizePlatformLanguage(lng));
    };

    setCurrentLang(normalizePlatformLanguage(i18nInstance.language));

    // Listen for language changes
    i18nInstance.on('languageChanged', handleLanguageChange);

    return () => {
      i18nInstance.off('languageChanged', handleLanguageChange);
    };
  }, [i18nInstance]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = async (newLang) => {
    if (newLang === currentLang || isChanging) {
      return;
    }

    setIsChanging(true);
    setIsOpen(false);

    try {
      await applyPlatformLanguage(newLang);
    } catch (error) {
      console.error('Error changing language:', error);
      alert('Failed to change language. Please try again.');
    } finally {
      setIsChanging(false);
    }
  };

  // English, Arabic, and French - using translations
  const languages = [
    { code: 'en', name: t('language.english', { ns: 'common' }) },
    { code: 'ar', name: t('language.arabic', { ns: 'common' }) },
    { code: 'fr', name: t('language.french', { ns: 'common' }) },
  ];

  return (
    <div className={`language-switcher ${className}`} ref={dropdownRef}>
      <button
        className="language-switcher-trigger"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isChanging}
        title={t('language.switchLanguage', { ns: 'common' })}
        aria-label={t('language.switchLanguage', { ns: 'common' })}
      >
        <span className="language-name-display">
          {languages.find(lang => lang.code === currentLang)?.name || (currentLang === 'ar' ? 'العربية' : 'English')}
        </span>
        <i className="material-icons">arrow_drop_down</i>
      </button>

      {isOpen && (
        <div className="language-switcher-dropdown">
          {languages.map((lang) => (
            <button
              key={lang.code}
              className={`language-option ${currentLang === lang.code ? 'active' : ''}`}
              onClick={() => handleLanguageChange(lang.code)}
              disabled={isChanging}
            >
              <span className="language-name">{lang.name}</span>
              {currentLang === lang.code && (
                <i className="material-icons">check</i>
              )}
            </button>
          ))}
        </div>
      )}

      {isChanging && (
        <div className="language-switcher-loading">
          <i className="material-icons spinning">hourglass_empty</i>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;

